import { FC, useMemo } from 'react';
import { Tooltip } from 'hudskit-framework-react';

import FAQIcon from 'assets/vectors/iconsFAQ.svg';
import QuickGuideIcon from 'assets/vectors/iconsNotificationInfoOutlined.svg';
import ManualsIcon from 'assets/vectors/iconsProductTechSpec.svg';
import Badge from 'components/UI/Badge';
import { IconLink } from 'components/UI/IconLink';
import { ProductDocumentData } from 'models/product';

import { Badges, Container, Text, TooltipContent } from './styled';

const validTypes = {
  WM: 'Workshop manual',
  DOC: 'Declaration of Conformity',
  OM: 'Operators manual',
  AI: 'Assembly Instruction',
  IPL: 'Illustrated Parts List',
  QG: 'Quick Guide',
  RD: 'Regulatory Documents',
  TH: 'Technical manual',
  SB: 'Service bulletin',
  SMB: 'Service manager bulletin',
  SP: 'Service Plan',
  TD: 'Transport Documents',
  FAQ: 'Frequently asked questions',
  SDS: 'Security Data Sheet',
  CTC: 'Commercial Terms & Conditions',
  CMP: 'Commercial Maintenance Plan',
  OMS: 'Operators Manual Slim',
  PEC: 'Product Error Codes',
  RN: 'Release Note'
};

type ValidDocumentKeys = keyof typeof validTypes;

type Props = {
  document: ProductDocumentData;
};

export const ProductDocument: FC<Props> = ({ document }) => {
  // Languages
  const languages = document.languages ?? [];
  const visibleLanguages = languages.slice(0, 3);
  const remainingLanguages = languages.slice(3);

  // Document type
  const documentType =
    validTypes[document?.publicationType as ValidDocumentKeys];

  // Render icon depending on type
  const iconSrc = useMemo(() => {
    switch (documentType) {
      case validTypes.QG:
        return QuickGuideIcon;
      case validTypes.FAQ:
        return FAQIcon;
      default:
        return ManualsIcon;
    }
  }, [documentType]);

  return (
    <IconLink link={document.link} iconSrc={iconSrc}>
      <Container>
        <Text>
          {document.publicationTitle.en ?? documentType ?? 'Document'}
        </Text>
        <Badges>
          {document.format && <Badge>{document.format.toUpperCase()}</Badge>}
          <Tooltip
            content={<TooltipContent>{languages.join(', ')}</TooltipContent>}
          >
            <Badges>
              {visibleLanguages.map((lang) => (
                <Badge key={lang}>{lang}</Badge>
              ))}
              {remainingLanguages.length > 0 && (
                <Badge>{`+${remainingLanguages.length}`}</Badge>
              )}
            </Badges>
          </Tooltip>
        </Badges>
      </Container>
    </IconLink>
  );
};
